import type {
  IApplyLastMessageAtToChatListProps,
  IChatListPagesContainer,
  IChatListRemoval,
  IChatListRowRemoval,
  ICollectChatListRemovalsProps,
  IFindChatListRemovalProps,
  IGetLatestMessageTimestampProps,
  IIsIncomingChatTimestampNewerProps,
  IPatchChatListPagesProps,
  IPatchChatListPagesResult,
  IRemoveChatFromChatListProps,
  IRemoveChatFromListPagesProps,
  IRestoreChatListRowsProps,
  IRestoreChatToChatListProps,
  IRestoreChatToListPagesProps,
  ISyncChatListLastMessageAtProps,
} from "../types/chat";
import { EGenerationType } from "../types/chat";

// NOTE: NSAI-4601 (PR review follow-up) — these generation types are never
// passed through chat-message-persistence.service.ts's saveMessageEntity —
// the only server-side call site of touchChatLastMessageAt. multi-model-
// summary.service.ts and chat-branch.service.ts both save their message
// directly via messageRepository.save and broadcast "newMessages" for it, so
// its createdAt is real wall-clock time that does NOT correspond to a bump
// of chats.last_message_at. Treating it as the chat's new activity timestamp
// would patch the sidebar cache with a value the server never wrote — a
// forward drift isIncomingChatTimestampNewer can't catch, since it only
// blocks an incoming timestamp OLDER than the cached one. The next real list
// invalidation then reverts the cache, reproducing the exact staleness bug
// this file exists to fix, just via a different trigger.
const GENERATION_TYPES_WITHOUT_LAST_MESSAGE_AT_BUMP: ReadonlySet<EGenerationType> =
  new Set([EGenerationType.Summary, EGenerationType.BranchContext]);

const isEligibleForLastMessageAt = (message: {
  generationType?: EGenerationType;
}): boolean =>
  !message.generationType ||
  !GENERATION_TYPES_WITHOUT_LAST_MESSAGE_AT_BUMP.has(message.generationType);

// NOTE: NSAI-4601 — the platform-agnostic half of a chat-list cache patch:
// find the chat by uuid across the cached pages (it can live in at most one)
// and merge `patch` onto it, rebuilding only that page so every other page
// keeps its reference — cheap for consumers that diff by reference (e.g.
// React).
export const patchChatListPages = <TPatch>(
  params: IPatchChatListPagesProps<TPatch>,
): IPatchChatListPagesResult => {
  const { pages, chatUuid, patch, shouldPatch } = params;

  for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
    const page = pages[pageIndex];
    const itemIndex = page.data.findIndex(
      (chatItem) => chatItem.uuid === chatUuid,
    );
    if (itemIndex === -1) continue;

    // NOTE: The chat can only live in one page, so once it's found there is
    // nothing left to scan — whether or not the guard below lets it patch.
    const chatItem = page.data[itemIndex];
    if (shouldPatch && !shouldPatch(chatItem)) break;

    const nextData = [...page.data];
    nextData[itemIndex] = { ...chatItem, ...patch };

    const newPages = [...pages];
    newPages[pageIndex] = { ...page, data: nextData };
    return { pages: newPages, wasUpdated: true };
  }

  return { pages, wasUpdated: false };
};

// NOTE: NSAI-4601 — a delayed/out-of-order/replayed socket event must not
// regress an already-newer cached timestamp. Missing or unparseable inputs
// fall back to "newer" (permissive) rather than blocking a real update.
export const isIncomingChatTimestampNewer = (
  params: IIsIncomingChatTimestampNewerProps,
): boolean => {
  const { currentTimestamp, incomingTimestamp } = params;
  if (!currentTimestamp) return true;

  const current = new Date(currentTimestamp).getTime();
  const incoming = new Date(incomingTimestamp).getTime();
  if (Number.isNaN(current) || Number.isNaN(incoming)) return true;

  return incoming >= current;
};

// NOTE: NSAI-4601 (PR review follow-up) — a "newMessages" batch isn't
// guaranteed to be createdAt-sorted (see IGetLatestMessageTimestampProps),
// so the chat-list patch must scan the whole batch for the true latest
// activity instead of trusting the last array element. Falls back to the
// last eligible message's createdAt if every timestamp in the batch is
// unparseable — permissive, matching isIncomingChatTimestampNewer's fallback
// philosophy. Returns undefined when no message in the batch is eligible
// (see GENERATION_TYPES_WITHOUT_LAST_MESSAGE_AT_BUMP above) — there is
// nothing real to sync in that case, not "no timestamp found".
export const getLatestMessageTimestamp = (
  params: IGetLatestMessageTimestampProps,
): string | undefined => {
  const { messages } = params;
  const eligibleMessages = messages.filter(isEligibleForLastMessageAt);

  let latestTimestamp: string | undefined;
  let latestTime = -Infinity;

  for (const message of eligibleMessages) {
    const time = new Date(message.createdAt).getTime();
    if (Number.isNaN(time)) continue;

    if (time >= latestTime) {
      latestTime = time;
      latestTimestamp = message.createdAt;
    }
  }

  return latestTimestamp ?? eligibleMessages.at(-1)?.createdAt;
};

// NOTE: NSAI-4601 (PR review follow-up, Sonar new-code duplication) — this
// used to be hand-copied into web's chat-cache-utils.ts and mobile's
// use-chat-web-socket.ts as a thin wrapper around patchChatListPages, on the
// assumption that the InfiniteData<IChatListPage> shape wasn't a
// frontend-shared dependency. It doesn't need to be: the function only ever
// reads/spreads `oldData.pages`, so it's generic over any TData shaped like
// IChatListPagesContainer — InfiniteData<IChatListPage> qualifies
// structurally without frontend-shared importing @tanstack/react-query.
//
// The sidebar sorts/groups by lastMessageAt (getChatActivityDate /
// groupChatsByDate), but a new message only ever wrote into the chat-history
// cache, leaving the chat-list entry's lastMessageAt stale until something
// else invalidated the whole list (reload, pin, delete, ...). This patches
// just the matching chat, guarded against a delayed/out-of-order event
// regressing an already-newer cached value (isIncomingChatTimestampNewer) —
// a multi-model turn saves its assistant rows via Promise.all (input order,
// not save-completion order), and some emitters (e.g. the multi-model
// summary placeholder) never went through the server's own
// touchChatLastMessageAt write at all.
export const applyLastMessageAtToChatList = <
  TData extends IChatListPagesContainer,
>(
  params: IApplyLastMessageAtToChatListProps<TData | undefined>,
): TData | undefined => {
  const { oldData, chatUuid, lastMessageAt } = params;
  if (!oldData) return oldData;

  const { pages, wasUpdated } = patchChatListPages({
    pages: oldData.pages,
    chatUuid,
    patch: { lastMessageAt },
    shouldPatch: (chatItem) =>
      isIncomingChatTimestampNewer({
        currentTimestamp: chatItem.lastMessageAt,
        incomingTimestamp: lastMessageAt,
      }),
  });

  if (!wasUpdated) return oldData;

  return { ...oldData, pages };
};

// NOTE: NSAI-4601 (PR review follow-up, Sonar new-code duplication) — the
// handleNewMessage call site (compute the batch's latest createdAt, then
// patch every cached chat-list variant via setQueriesData) used to be
// hand-copied into web's and mobile's use-chat-web-socket.ts verbatim,
// comments included. Collapsed into one call so a future change to this
// flow (e.g. the query-key scope) only has one place to land. setQueriesData
// (not setQueryData) patches every cached chat-list variant — the root
// sidebar list, plus any folder- or search-scoped list — not just the exact
// key passed in.
export const syncChatListLastMessageAt = <
  TData extends IChatListPagesContainer,
>(
  params: ISyncChatListLastMessageAtProps,
): void => {
  const { queryClient, chatListQueryKey, messages, chatUuid } = params;

  const latestMessageCreatedAt = getLatestMessageTimestamp({ messages });
  // NOTE: NSAI-4601 (PR review follow-up) — every message in the batch was
  // ineligible (Summary/BranchContext — see
  // GENERATION_TYPES_WITHOUT_LAST_MESSAGE_AT_BUMP), so there is no real
  // last_message_at bump to sync. Leave the cache untouched rather than
  // patching it with a timestamp the server never wrote.
  if (!latestMessageCreatedAt) return;

  queryClient.setQueriesData<TData>({ queryKey: chatListQueryKey }, (oldData) =>
    applyLastMessageAtToChatList<TData>({
      oldData,
      chatUuid,
      lastMessageAt: latestMessageCreatedAt,
    }),
  );
};

// NOTE: NSAI-4686 — drop a chat out of the cached pages entirely, the removal
// counterpart of patchChatListPages. Untouched pages keep their reference, and
// so does the whole array when the chat was not cached at all, so a consumer
// that diffs by reference re-renders only the list the chat actually left.
//
// Unlike patchChatListPages this scans EVERY page instead of stopping at the
// first match. The chat list is paginated by offset, so while a refetch is in
// flight the same chat can legitimately sit in two pages at once (NSAI-4792,
// which is why dedupeChatsByUuid exists). Stopping at the first hit would leave
// the duplicate on screen and the row would not disappear — precisely the thing
// the caller is removing it to achieve.
export const removeChatFromListPages = (
  params: IRemoveChatFromListPagesProps,
): IPatchChatListPagesResult => {
  const { pages, chatUuid } = params;
  let wasUpdated = false;

  const newPages = pages.map((page) => {
    const nextData = page.data.filter((chatItem) => chatItem.uuid !== chatUuid);
    if (nextData.length === page.data.length) return page;

    wasUpdated = true;
    return { ...page, data: nextData };
  });

  if (!wasUpdated) return { pages, wasUpdated: false };

  return { pages: newPages, wasUpdated: true };
};

// NOTE: NSAI-4686 — the cache-wrapper half of the above, shaped like
// applyLastMessageAtToChatList so both apps can hand it straight to
// setQueriesData. Generic over IChatListPagesContainer for the same reason:
// InfiniteData<IChatListPage> satisfies it structurally, so frontend-shared
// still never imports @tanstack/react-query.
//
// Dropping a chat on a folder used to leave its row sitting in the old list
// until the server answered and the list refetched, so the move read as a
// refusal followed by a jump. Callers now remove the row when the drop is
// accepted and restore this snapshot if the request fails.
export const removeChatFromChatList = <TData extends IChatListPagesContainer>(
  params: IRemoveChatFromChatListProps<TData | undefined>,
): TData | undefined => {
  const { oldData, chatUuid } = params;
  if (!oldData) return oldData;

  const { pages, wasUpdated } = removeChatFromListPages({
    pages: oldData.pages,
    chatUuid,
  });

  if (!wasUpdated) return oldData;

  return { ...oldData, pages };
};

// NOTE: NSAI-4686 (PR review) — the position a chat is about to be removed
// from, captured before the optimistic write so a failure can put that row back
// without touching anything else in the cache. Returns the FIRST page the chat
// is found in: offset pagination can duplicate it across a page boundary while
// a refetch is in flight (NSAI-4792), and re-inserting it once is enough — the
// duplicate was an artifact of that refetch, not state worth restoring.
export const findChatListRemoval = (
  params: IFindChatListRemovalProps,
): IChatListRemoval | undefined => {
  const { pages, chatUuid } = params;

  let removal: IChatListRemoval | undefined;

  for (
    let pageIndex = 0;
    pageIndex < pages.length && !removal;
    pageIndex += 1
  ) {
    const page = pages[pageIndex];
    const itemIndex = page.data.findIndex(
      (chatItem) => chatItem.uuid === chatUuid,
    );
    if (itemIndex === -1) continue;

    removal = { pageIndex, itemIndex, chat: page.data[itemIndex] };
  }

  return removal;
};

// NOTE: NSAI-4686 (PR review) — the inverse of removeChatFromListPages, applied
// to whatever the cache holds NOW rather than to the snapshot the removal was
// taken from. That is the whole point: pages fetched while the request was in
// flight, and rows other mutations moved in the meantime, survive the rollback.
//
// Two guards. The chat is not re-inserted if it is already cached again — a
// refetch that beat the rollback home would otherwise leave the list showing it
// twice. And both indexes are clamped, since the page it came out of may have
// been trimmed since; the row lands as close to where it was as the current
// pages allow, and the invalidation that follows settles the exact order.
export const restoreChatToListPages = (
  params: IRestoreChatToListPagesProps,
): IPatchChatListPagesResult => {
  const { pages, removal } = params;

  const isChatAlreadyCached = pages.some((page) =>
    page.data.some((chatItem) => chatItem.uuid === removal.chat.uuid),
  );
  if (isChatAlreadyCached) return { pages, wasUpdated: false };

  const pageIndex = Math.min(removal.pageIndex, pages.length - 1);
  if (pageIndex < 0) return { pages, wasUpdated: false };

  const page = pages[pageIndex];
  const itemIndex = Math.min(removal.itemIndex, page.data.length);

  const nextData = [...page.data];
  nextData.splice(itemIndex, 0, removal.chat);

  const newPages = [...pages];
  newPages[pageIndex] = { ...page, data: nextData };

  return { pages: newPages, wasUpdated: true };
};

// NOTE: NSAI-4686 (PR review) — the cache-wrapper half of the above, shaped like
// removeChatFromChatList so a mutation's onError can hand it straight to
// setQueryData. A query that is gone from the cache entirely is left alone:
// there is no list to put the row back into, and the invalidation on settle
// refetches it from scratch anyway.
export const restoreChatToChatList = <TData extends IChatListPagesContainer>(
  params: IRestoreChatToChatListProps<TData | undefined>,
): TData | undefined => {
  const { oldData, removal } = params;
  if (!oldData) return oldData;

  const { pages, wasUpdated } = restoreChatToListPages({
    pages: oldData.pages,
    removal,
  });

  if (!wasUpdated) return oldData;

  return { ...oldData, pages };
};

// NOTE: NSAI-4686 (PR review) — the onMutate half of a drop's rollback plan:
// walk every cached chat list the mutation is about to sweep and write down
// where the row sits in each. Shared because all three drop mutations do exactly
// this, and because it is the piece that has to stay in step with
// restoreChatListRows below.
export const collectChatListRemovals = <TData extends IChatListPagesContainer>(
  params: ICollectChatListRemovalsProps<TData>,
): IChatListRowRemoval[] => {
  const { cachedChatLists, chatUuid } = params;

  return cachedChatLists.flatMap(([queryKey, data]) => {
    const removal = findChatListRemoval({
      pages: data?.pages ?? [],
      chatUuid,
    });

    return removal ? [{ queryKey, removal }] : [];
  });
};

// NOTE: NSAI-4686 (PR review) — the onError half. Each row goes back into
// whatever its query holds at the moment the failure arrives, never into the
// snapshot it was taken from, so anything that landed in the meantime survives.
export const restoreChatListRows = <TData extends IChatListPagesContainer>(
  params: IRestoreChatListRowsProps,
): void => {
  const { queryClient, rows } = params;

  rows.forEach(({ queryKey, removal }) => {
    queryClient.setQueryData<TData>(queryKey, (oldData) =>
      restoreChatToChatList<TData>({ oldData, removal }),
    );
  });
};
